import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { TQuizAnswerData } from '@lib/core/quizzes/types';
import { localeV2 } from '@lib/tools/locale/source/pmi/v2';
import { LocaleFragment } from '@lib/tools/locale/views/LocaleFragment';
import { useHeight } from '@lib/tools/views/hooks';

import { Button } from '@components/pmi/src/atoms/CTA/Button';
import { HealthWarningFooterToIsrael } from '@components/pmi/src/common/HealthWarningFooterToIsrael';
import { useCurrentQuizBackground } from '@components/pmi/src/hooks/useCurrentQuizBackground';
import { Header } from '@components/pmi/src/organisms/Header/Header';
import { HealthWarningFooter } from '@components/pmi/src/organisms/HealthWarning/HealthWarning';
import { AnswerSectionHeader } from '@components/pmi/src/organisms/Quiz/AnswerSectionHeader/AnswerSectionHeader';
import { AnswerItem } from '@components/pmi/src/organisms/Quiz/QDS8005/AnswerItem/AnswerItem';
import { QuizFooter } from '@components/pmi/src/organisms/Quiz/QuizFooter/QuizFooter';

interface Props {
  isLight?: boolean;
  currentPrimaryColor: string;
  questionImage: string;
  questionTitle: string;
  questionCaption: string;
  isShowBackButton: boolean;
  isLoading: boolean;
  goBack: () => void;
  progressPercent: number;
  selectedChoicesCount: number;
  answers: TQuizAnswerData[];
  choicesQuantity: {
    maxChoices: number;
    minChoices: number;
  };
  handleConfirmAnswer: (answerId: string | string[], callback: () => void) => void;
  isHeetsBackgroundEnabled: boolean;
  isHealthWarningQuizAddon: boolean;
  isShowRestrictedBackground: boolean;
  isHealthWarningFooterToIsraelAddon?: boolean;
}

export const QuizPageQDS8005 = ({
  isLight = false,
  currentPrimaryColor,
  questionTitle,
  questionCaption,
  isLoading,
  questionImage,
  goBack,
  isShowBackButton,
  progressPercent,
  answers,
  choicesQuantity,
  handleConfirmAnswer,
  isHeetsBackgroundEnabled,
  isHealthWarningQuizAddon,
  isShowRestrictedBackground,
  isHealthWarningFooterToIsraelAddon,
}: Props) => {
  const [footerRef, paddingForFooter] = useHeight<HTMLDivElement>();
  const minChoicesQuantity = choicesQuantity?.minChoices;
  const maxAnswersChoices = choicesQuantity?.maxChoices;
  const isMobileResolution = useMediaQuery({ maxWidth: 767 });
  const [answersArray, setAnswersArray] = useState<string[]>([]);
  const currentBackground = useCurrentQuizBackground(questionImage, isShowRestrictedBackground);
  const handleClick = () => {
    handleConfirmAnswer(answersArray, () => {
      setAnswersArray([]);
    });
  };

  return (
    <div
      className={`ta-v2-qds8005-page ${isHealthWarningFooterToIsraelAddon ? 'padding-bottom-30vh minh-101vh' : 'minh-100vh'}`}
      style={{
        background: currentBackground,
        paddingBottom: paddingForFooter,
      }}
    >
      <Header
        caption={questionCaption}
        currentPrimaryColor={currentPrimaryColor}
        goBack={goBack}
        isHeetsBackgroundEnabled={isHeetsBackgroundEnabled}
        isLoading={isLoading}
        isShowBackButton={isShowBackButton}
        progressPercent={progressPercent}
        title={questionTitle}
      />
      <div className="ta-v2-qds8005-page-answer-section-cover">
        <div className="ta-v2-qds8005-page-answer-section">
          <AnswerSectionHeader
            isLight={isLight}
            maxChoicesCount={maxAnswersChoices}
            selectedChoicesCount={answersArray?.length || 0}
          />
          <div className="ta-v2-qds8005-page-answer-item-container">
            {answers.map(answer => {
              const answerId = Object.keys(answer)[0];
              return (
                <AnswerItem
                  key={answerId}
                  answerId={answerId}
                  answerText={answer[answerId].text}
                  answerVideo={answer[answerId].image}
                  answersArray={answersArray}
                  className="ta-v2-qds8005-page-answer-item"
                  currentPrimaryColor={currentPrimaryColor}
                  maxAnswersChoices={maxAnswersChoices}
                  setAnswersArray={setAnswersArray}
                />
              );
            })}
          </div>
        </div>
        {!isMobileResolution ? (
          <div className="d-flex justify-content-center w-100 ta-v2-cta-btn-margin-large">
            <Button
              btnText={<LocaleFragment message={localeV2.common.nextQuestion} />}
              isDark={answersArray.length >= minChoicesQuantity}
              isDisabled={answersArray.length !== minChoicesQuantity}
              onBtnClick={handleClick}
            />
          </div>
        ) : null}
      </div>
      <div ref={footerRef} className="ta-v2-quiz-footer-wrapper">
        {isMobileResolution ? (
          <QuizFooter handleClick={handleClick} isNextButtonEnabled={answersArray.length >= minChoicesQuantity} />
        ) : null}
        {isHealthWarningQuizAddon && <HealthWarningFooter />}
      </div>
      {isHealthWarningFooterToIsraelAddon && <HealthWarningFooterToIsrael />}
    </div>
  );
};
